import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  MenuProps,
  Modal,
  ModalProps,
  Row,
  Select,
  Table
} from 'antd'
import { AnyObject } from 'antd/es/_util/type'
import { ColumnsType } from 'antd/es/table'
import { TFunction } from 'i18next'
import { useEffect, useState } from 'react'

import {
  useClassifierServiceCreateInvestigationClassifierEndpoint,
  useClassifierServiceGetInvestigationClassifierEndpoint,
  useClassifierServiceGetInvestigationClassifierEndpointKey,
  useClassifierServiceGetInvestigationClassifierListEndpointKey,
  useClassifierServiceUpdateInvestigationClassifierEndpoint
} from 'src/api/queries'
import {
  ApiError,
  ClassifierCategory,
  ClassifierType,
  InvestigationTypeModule,
  InvestigationTypePersonType
} from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import {
  ClassifierInfiniteSelect,
  EditableFormItem,
  message,
  SaveConfirm,
  TableActionButtons
} from 'src/components'
import { useClassifierId } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { EquipmentInfiniteSelect } from './EquipmentInfiniteSelect'
import { TestTypeModal } from './TestTypeModal'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean, t: TFunction) => {
  return {
    title: hasId
      ? t('laboratory_classifiers.edit_investigation_type')
      : t('laboratory_classifiers.add_investigation_type'),
    button: hasId ? t('common.edit') : t('common.save')
  }
}

export const InvestigationTypeModal = ({ id, onCancel, ...props }: Props) => {
  const { t, currentLang } = useLocales()

  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const [testTypes, setTestTypes] = useState<AnyObject[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [currentTestIndex, setCurrentTestIndex] = useState<number>()

  const investigationCategoryClassifierId = useClassifierId(
    ClassifierType.INVESTIGATION_CATEGORY,
    ClassifierCategory.INVESTIGATIONS
  )

  const workMethodIdClassifierId = useClassifierId(
    ClassifierType.WORK_METHOD,
    ClassifierCategory.INVESTIGATIONS
  )

  const workMethodDomainIdClassifierId = useClassifierId(
    ClassifierType.INVESTIGATION_WORK_METHOD_DOMAIN,
    ClassifierCategory.INVESTIGATIONS
  )

  const specimenTypeClassifierId = useClassifierId(
    ClassifierType.SPECIMEN_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )

  const specimenStorageConditionClassifierId = useClassifierId(
    ClassifierType.SPECIMEN_STORAGE_CONDITION,
    ClassifierCategory.INVESTIGATIONS
  )

  const medicalSpecialityId = useClassifierId(
    ClassifierType.MEDICAL_SPECIALTY,
    ClassifierCategory.INVESTIGATIONS
  )

  const typeOptions = Object.values(InvestigationTypeModule).map((item) => ({
    value: item,
    label: t(`laboratory_classifiers.type.${item}`)
  }))

  const personOptions = Object.values(InvestigationTypePersonType).map(
    (item) => ({
      value: item,
      label: t(`laboratory_classifiers.person.${item}`)
    })
  )

  const { title, button } = getModalText(!!id, t)

  const { data } = useClassifierServiceGetInvestigationClassifierEndpoint(
    {
      id: String(id)
    },
    [id],
    {
      enabled: !!id
    }
  )

  const { mutate: create, isPending: isCreating } =
    useClassifierServiceCreateInvestigationClassifierEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(
          t('laboratory_classifiers.create_success_investigation_type')
        )
        onMutationSuccess()
      }
    })

  const { mutate: edit, isPending: isEditing } =
    useClassifierServiceUpdateInvestigationClassifierEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        message.success(
          t('laboratory_classifiers.edit_success_investigation_type')
        )
        onMutationSuccess()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()

    const payload = {
      ...values,
      testTypes
    }

    return id
      ? edit({ id: String(id), requestBody: payload })
      : create({ requestBody: payload })
  }

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useClassifierServiceGetInvestigationClassifierListEndpointKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useClassifierServiceGetInvestigationClassifierEndpointKey]
    })
    onCancel()
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  const onCloseModal = () => {
    onToggle()
    setCurrentTestIndex(undefined)
    form.resetFields([
      'infectionId',
      'infection',
      'resultUnitMeasureId',
      'resultTypeId',
      'resultReferenceIntervalIds',
      'resultOptions',
      'type',
      'id'
    ])
  }

  const getActionItems = (index: number): MenuProps['items'] => {
    return [
      {
        label: t('common.edit'),
        onClick: () => setCurrentTestIndex(index),
        key: 'edit'
      },
      {
        key: 'divider',
        type: 'divider'
      },
      {
        label: t('common.delete'),
        onClick: () => {
          setTestTypes((prev) => prev.filter((_, i) => i !== index))
        },
        key: 'delete',
        danger: true
      }
    ]
  }

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        ...data,
        categoryId: data?.category?.id,
        equipmentId: data?.equipment?.id,
        workMethodId: data?.workMethod?.id,
        workMethodDomainId: data?.workMethodDomain?.id,
        specimenTypeIds: data?.specimenTypes?.map((item) => item.id),
        specimenStorageConditionId: data?.specimenStorageCondition?.id,
        testTypes: undefined,
        medicalSpecialityId: data?.medicalSpeciality?.id
      })

      if (data?.testTypes) {
        setTestTypes(
          data?.testTypes.map((item) => ({
            // Kept so the update edits the test type in place instead of re-creating it.
            id: item.id,
            infection: item.infection?.translation?.[currentLang?.value],
            infectionId: item.infection?.id,
            resultUnitMeasureId: item.resultUnitMeasure?.id,
            resultTypeId: item.resultType?.id,
            resultReferenceIntervalIds: item.resultReferenceIntervals?.map(
              (item) => item.id
            ),
            type: item.type,
            resultOptions: item.resultOptions?.map((item) => ({
              id: item?.id,
              valueOptionIds: item.valueOptions?.map((item) => item?.id)
            }))
          }))
        )
      }
    }
  }, [data])

  useEffect(() => {
    if (currentTestIndex !== undefined) {
      setIsOpen(true)

      const currentTest = testTypes[currentTestIndex]

      form.setFieldsValue(currentTest)
    }
  }, [currentTestIndex])

  const columns: ColumnsType = [
    {
      key: 'infection',
      title: t('laboratory_classifiers.investigation_infection'),
      dataIndex: 'infection'
    },
    {
      key: 'type',
      title: t('laboratory_classifiers.test_type_category'),
      render: (_, record) =>
        record.type
          ? t(
              `laboratory_classifiers.test_type_category_options.${record.type}`
            )
          : '-'
    },
    {
      key: 'actions',
      render: (_, __, index) => (
        <TableActionButtons justify="flex-end" items={getActionItems(index)} />
      )
    }
  ]

  return (
    <Modal
      open
      width={800}
      maskClosable={false}
      onCancel={onCancel}
      title={title}
      footer={null}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={[24, 24]}>
          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['title', langKey]}
                  label={`${t('laboratory_classifiers.investigation_title')} ${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['displayName', langKey]}
                  label={`${t('laboratory_classifiers.investigation_short_title')} ${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          {investigationCategoryClassifierId && (
            <Col span={12}>
              <Form.Item
                name="categoryId"
                label={t('laboratory_classifiers.investigation_category')}
                rules={requiredRules}
              >
                <ClassifierInfiniteSelect
                  id={investigationCategoryClassifierId}
                  placeholder={t(
                    'laboratory_classifiers.investigation_category'
                  )}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={12}>
            <Form.Item
              name="equipmentId"
              label={t('organizational_structure.equipment')}
              rules={requiredRules}
            >
              <EquipmentInfiniteSelect
                placeholder={t('organizational_structure.equipment')}
              />
            </Form.Item>
          </Col>

          {workMethodIdClassifierId && (
            <Col span={12}>
              <Form.Item
                name="workMethodId"
                label={t('laboratory_classifiers.work_method')}
              >
                <ClassifierInfiniteSelect
                  id={workMethodIdClassifierId}
                  placeholder={t('laboratory_classifiers.work_method')}
                />
              </Form.Item>
            </Col>
          )}

          {workMethodDomainIdClassifierId && (
            <Col span={12}>
              <Form.Item
                name="workMethodDomainId"
                label={t('laboratory_classifiers.work_method_domain')}
                rules={requiredRules}
              >
                <ClassifierInfiniteSelect
                  id={workMethodDomainIdClassifierId}
                  placeholder={t('laboratory_classifiers.work_method_domain')}
                />
              </Form.Item>
            </Col>
          )}

          {specimenTypeClassifierId && (
            <Col span={12}>
              <Form.Item
                name="specimenTypeIds"
                label={t('consultations.specimen_type')}
                rules={requiredRules}
              >
                <ClassifierInfiniteSelect
                  id={specimenTypeClassifierId}
                  mode="multiple"
                  placeholder={t('consultations.specimen_type')}
                />
              </Form.Item>
            </Col>
          )}

          {specimenStorageConditionClassifierId && (
            <Col span={12}>
              <Form.Item
                name="specimenStorageConditionId"
                label={t('laboratory_classifiers.specimen_storage')}
                rules={requiredRules}
              >
                <ClassifierInfiniteSelect
                  id={specimenStorageConditionClassifierId}
                  placeholder={t('laboratory_classifiers.specimen_storage')}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={12}>
            <Form.Item
              name="allowedModules"
              label={t('laboratory_classifiers.allowed_modules')}
              rules={requiredRules}
            >
              <Select
                mode="multiple"
                placeholder={t('laboratory_classifiers.allowed_modules')}
                options={typeOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="allowedPersonTypes"
              label={t('laboratory_classifiers.allowed_persons')}
              rules={requiredRules}
            >
              <Select
                mode="multiple"
                placeholder={t('laboratory_classifiers.allowed_persons')}
                options={personOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="medicalSpecialityId"
              label={t('laboratory_classifiers.medical_speciality')}
              rules={requiredRules}
            >
              <ClassifierInfiniteSelect
                id={medicalSpecialityId}
                placeholder={t('laboratory_classifiers.medical_speciality')}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="code" label={t('common.code')}>
              <Input maxLength={5} placeholder={t('common.code')} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="order"
              label={t('common.order')}
              rules={requiredRules}
            >
              <InputNumber className="full-width" min={0} placeholder="0" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="isTb" valuePropName="checked">
              <Checkbox>{t('laboratory_classifiers.is_tb')}</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="isRapidTest" valuePropName="checked">
              <Checkbox>{t('laboratory_classifiers.is_rapid_test')}</Checkbox>
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="comments" label={t('common.comments')}>
              <Input.TextArea placeholder={t('common.comments')} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Card
              title={t('laboratory_classifiers.test_types')}
              extra={
                <Button onClick={onToggle}>
                  {t('laboratory_classifiers.add_test_type')}
                </Button>
              }
            >
              <Table
                rowKey={(record) => JSON.stringify(record)}
                dataSource={testTypes}
                columns={columns}
                pagination={false}
              />
            </Card>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${button}?`}
                onConfirm={onSave}
                okButtonProps={{ loading: isEditing || isCreating }}
              >
                <Button type="primary">{button}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>

      {isOpen && (
        <TestTypeModal
          currentTestIndex={currentTestIndex}
          setTestTypes={setTestTypes}
          onCancel={onCloseModal}
          form={form}
        />
      )}
    </Modal>
  )
}
